import { TabsData } from '../types/navigation';

export interface NavigationContext {
  activeTab: string | null;
  activeSubTab: string | null;
  activeProduct: string | null;
}

export function parseUrlToNavigationContext(pathname: string, tabsData: TabsData): NavigationContext {
  // Remove leading slash and split the path
  const pathSegments = pathname.replace(/^\//, '').split('/').filter(Boolean);
  
  if (pathSegments.length === 0) {
    return { activeTab: null, activeSubTab: null, activeProduct: null };
  }

  // Handle different URL patterns
  const [firstSegment, secondSegment] = pathSegments;

  // Check if first segment matches a subtab directly (like /collections/lucidity)
  for (const tab of tabsData.tabs) {
    if (tab.subtabs) {
      for (const subtab of tab.subtabs) {
        if (subtab.id === firstSegment) {
          // Check if second segment is a product
          if (secondSegment && subtab.products) {
            const product = subtab.products.find(p => p.id === secondSegment);
            if (product) {
              return {
                activeTab: tab.id,
                activeSubTab: subtab.id,
                activeProduct: product.id
              };
            }
          }
          // Just the subtab
          return {
            activeTab: tab.id,
            activeSubTab: subtab.id,
            activeProduct: null
          };
        }
      }
    }
  }

  // Check if first segment matches a tab directly
  const matchingTab = tabsData.tabs.find(tab => tab.id === firstSegment);
  if (matchingTab) {
    // If there's a second segment, check if it's a subtab or product
    if (secondSegment) {
      // Check subtabs first
      if (matchingTab.subtabs) {
        const matchingSubtab = matchingTab.subtabs.find(subtab => subtab.id === secondSegment);
        if (matchingSubtab) {
          return {
            activeTab: matchingTab.id,
            activeSubTab: matchingSubtab.id,
            activeProduct: null
          };
        }
        
        // Check if second segment is a product in any subtab
        for (const subtab of matchingTab.subtabs) {
          if (subtab.products) {
            const product = subtab.products.find(p => p.id === secondSegment);
            if (product) {
              return {
                activeTab: matchingTab.id,
                activeSubTab: subtab.id,
                activeProduct: product.id
              };
            }
          }
        }
      }
    }
    
    return {
      activeTab: matchingTab.id,
      activeSubTab: null,
      activeProduct: null
    };
  }

  // Handle special cases based on the routing structure
  // Collections routes like /collections/lucidity
  if (firstSegment === 'collections' && secondSegment) {
    return {
      activeTab: 'watches',
      activeSubTab: 'collections',
      activeProduct: secondSegment
    };
  }

  // Clockwork routes
  if (firstSegment === 'clockwork' && secondSegment) {
    return {
      activeTab: 'watches',
      activeSubTab: 'movements',
      activeProduct: secondSegment
    };
  }

  // Elements routes
  if (firstSegment === 'elements' && secondSegment) {
    return {
      activeTab: 'watches',
      activeSubTab: 'elements',
      activeProduct: secondSegment
    };
  }

  // Important objects routes
  if (firstSegment === 'important_objects' && secondSegment) {
    return {
      activeTab: 'important_objects',
      activeSubTab: null,
      activeProduct: secondSegment
    };
  }

  // Maison routes
  if (firstSegment === 'maison' && secondSegment) {
    return {
      activeTab: 'maison',
      activeSubTab: secondSegment,
      activeProduct: null
    };
  }

  // Commitments routes
  if (firstSegment === 'commitments' && secondSegment) {
    return {
      activeTab: 'maison',
      activeSubTab: 'commitments',
      activeProduct: secondSegment
    };
  }

  return { activeTab: null, activeSubTab: null, activeProduct: null };
}